import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, MessageSquare, PlugZap, Clock, TrendingUp, Users, ChevronDown, ChevronUp, Star, ArrowRight, Play, Mic, Zap, Shield, BarChart2, RefreshCw, CheckCircle2, AlertTriangle, Lightbulb, Target, PhoneOff } from 'lucide-react';
import WaitlistForm from '../components/landing/WaitlistForm';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Reveal animation wrapper
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: 'easeOut', delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── NAVBAR ────────────────────────────────────────────────────────────────
function Navbar({ onJoinClick }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/70 backdrop-blur-md border-b border-white/10 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/" className="hidden sm:flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors mr-1">
            ← Home
          </a>
          <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span className="text-white">Saliba</span><span style={{ color: '#C9A84C' }}>Labs</span><span className="text-white/50">.ai</span>
          </span>
          <span className="hidden sm:block px-2 py-0.5 rounded-full text-[10px] font-mono-accent tracking-widest border border-[#C9A84C]/25 text-[#C9A84C]">AUTOMOTIVE</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {['Story', 'How It Works', 'Results', 'FAQ'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/ /g, '-')}`}
              data-testid={`header-${item.toLowerCase().replace(/ /g, '-')}-link`}
              className="text-sm text-white/70 hover:text-white transition-colors relative group"
            >
              {item}
              <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-0 group-hover:w-full h-px bg-[#C9A84C]/60 transition-all duration-200" />
            </a>
          ))}
        </div>
        <button
          data-testid="header-join-waitlist-button"
          onClick={onJoinClick}
          className="text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 hover:shadow-[0_0_20px_rgba(201,168,76,0.4)]"
          style={{ background: '#C9A84C', color: '#0a0a0a' }}
        >
          Join Waitlist
        </button>
      </div>
    </nav>
  );
}

// ─── HERO ──────────────────────────────────────────────────────────────────
function Hero({ formRef }) {
  return (
    <section
      className="relative min-h-screen flex items-center pt-16"
      style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1662103628018-5ed41a5f2bca?crop=entropy&cs=srgb&fm=jpg&q=85)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlays */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(10,10,10,0.88) 50%, rgba(0,0,0,0.75) 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(900px circle at 15% 50%, rgba(201,168,76,0.12), transparent 55%)' }} />

      <div className="relative z-10 mx-auto w-[min(1120px,calc(100%-32px))] py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/8">
                <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
                <span className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase">@georgejsaliba — Stay Tuned</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/20 bg-red-500/8">
                <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                <span className="font-mono-accent text-xs text-red-400 tracking-widest uppercase">Limited Early Access</span>
              </div>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-[3.6rem] font-semibold leading-[1.08] tracking-tight mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              George Saliba’s
              <br />
              latest investment
              <br />
              <span className="gold-text">is changing the game.</span>
            </h1>

            <p className="text-base sm:text-lg text-white/65 leading-relaxed mb-8 max-w-xl">
              78% of sales go to the first responder. Most dealerships respond in 3+ hours.
              George Saliba invested in the technology that closes that gap — and now
              he’s opening it to a <span className="text-white font-medium">select group of dealers.</span>
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              {[
                { icon: <TrendingUp size={14} />, label: 'Backed by George Saliba' },
                { icon: <RefreshCw size={14} />, label: 'Uncover Hidden Revenue' },
                { icon: <Users size={14} />, label: 'Enhance Your Team' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-sm text-white/70">
                  <span style={{ color: '#C9A84C' }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            {/* Stats inline */}
            <div className="flex gap-8">
              {[{ v: '24/7', l: 'Always Working' }, { v: '100%', l: 'Leads Touched' }, { v: '+$$$', l: 'Revenue Uncovered' }].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold" style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}>{s.v}</div>
                  <div className="text-xs text-white/50 mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut', delay: 0.15 }}
          >
            <WaitlistForm variant="hero" />
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to bottom, transparent, #0a0a0a)' }} />
    </section>
  );
}

// ─── SOCIAL PROOF STRIP ──────────────────────────────────────────────────────
function SocialProof() {
  const chips = [
    { label: 'Uncover Hidden Revenue Opportunities', icon: <TrendingUp size={14} /> },
    { label: 'Enhance Your Team — Not Replace Them', icon: <Users size={14} /> },
    { label: 'Reactivate Dormant CRM Leads', icon: <RefreshCw size={14} /> },
    { label: 'Backed by George Saliba — 1M+ Followers', icon: <Star size={14} /> },
    { label: 'DriveCentric + vAuto Integrated', icon: <PlugZap size={14} /> },
    { label: '24/7 — Always Finding Opportunities', icon: <Zap size={14} /> },
    { label: 'Uncover Hidden Revenue Opportunities', icon: <TrendingUp size={14} /> },
    { label: 'Enhance Your Team — Not Replace Them', icon: <Users size={14} /> },
  ];
  return (
    <div data-testid="social-proof-strip" className="border-y border-white/8 bg-black/40 py-4 overflow-hidden">
      <div className="flex gap-4 animate-[scroll_25s_linear_infinite] whitespace-nowrap" style={{ width: 'max-content' }}>
        {chips.map((chip, i) => (
          <div
            key={i}
            className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/3 text-sm text-white/75"
          >
            {chip.icon && <span style={{ color: '#C9A84C' }}>{chip.icon}</span>}
            {chip.label}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ─── GEORGE STORY ────────────────────────────────────────────────────────────
function GeorgeStory() {
  return (
    <section id="story" data-testid="george-story-section" className="section-anchor py-24 lg:py-32">
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">The Innovator Behind It</div>
          <h2 className="text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl" style={{ fontFamily: 'Space Grotesk' }}>
            He saw the opportunity hiding in plain sight.
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <Reveal delay={0.1}>
            <div className="relative">
              <div
                className="aspect-[4/5] rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 0 0 1px rgba(201,168,76,0.15), 0 32px 80px rgba(0,0,0,0.7)' }}
              >
                <img
                  src="https://ugc.production.linktr.ee/c1557bd3-0db3-4f04-a35b-b6332f19a89e_IMG-0412.jpeg"
                  alt="George Saliba — The Auto Guy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)' }} />
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>George Saliba</div>
                <div className="text-sm text-[#C9A84C] font-mono-accent">Investor &amp; Advisor — SalibaLabs.ai</div>
                <div className="flex items-center gap-3 mt-2">
                  <a href="https://www.youtube.com/@georgejsaliba" target="_blank" rel="noopener noreferrer" data-testid="social-proof-youtube-link" className="text-xs text-white/60 hover:text-white transition-colors">YouTube ↗</a>
                  <a href="https://www.instagram.com/georgejsaliba" target="_blank" rel="noopener noreferrer" data-testid="social-proof-instagram-link" className="text-xs text-white/60 hover:text-white transition-colors">Instagram ↗</a>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Story copy */}
          <Reveal delay={0.2}>
            <div className="space-y-6">
              <div className="relative pl-5 border-l-2 border-[#C9A84C]/50">
                <blockquote className="text-xl text-white/90 leading-relaxed italic" style={{ fontFamily: 'Space Grotesk' }}>
                  &ldquo;There&rsquo;s revenue sitting in every CRM that never gets touched.
                  Great salespeople, great inventory — but opportunities slipping away
                  because the system wasn&rsquo;t built to find them.
                  That&rsquo;s what SalibaLabs changes.&rdquo;
                </blockquote>
                <cite className="font-mono-accent text-xs text-[#C9A84C] mt-3 block not-italic">— George Saliba, Investor &amp; Advisor, SalibaLabs.ai</cite>
              </div>

              <div className="gold-divider" />

              <div className="space-y-4 text-white/70 text-base leading-relaxed">
                <p>
                  George Saliba has 1M+ followers because he tells the truth about the car business.
                  His truth: great teams leave money on the table every day — not because they&apos;re bad at their jobs,
                  but because there&apos;s more opportunity in every dealership than any human team can pursue alone.
                </p>
                <p>
                  Leads that came in three months ago and went cold. Customers who inquired but never bought.
                  Inbound calls after hours with nobody to answer. Every single one is a deal waiting to happen.
                  SalibaLabs is the agent that finds them, warms them up, and brings them back to life.
                </p>
                <p>
                  <span className="text-white font-medium">This is not about replacing your people.</span>
                  {' '}It&apos;s about giving them more opportunities to close — without burning them out or adding headcount.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-2">
                {[
                  { icon: <TrendingUp size={20} />, label: '1M+ Followers', sub: 'Trusted automotive voice' },
                  { icon: <Lightbulb size={20} />, label: 'Innovator', sub: 'Investing in what\'s next' },
                  { icon: <Star size={20} />, label: 'Skin in the Game', sub: 'Investor, not observer' },
                ].map((item, i) => (
                  <div key={i} className="p-3 rounded-xl border border-white/8 bg-white/3 text-center">
                    <div className="flex justify-center mb-1.5" style={{ color: '#C9A84C' }}>{item.icon}</div>
                    <div className="text-sm font-semibold text-white">{item.label}</div>
                    <div className="text-xs text-white/50 mt-0.5">{item.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ─── WHAT IT DOES ───────────────────────────────────────────────────────────
function WhatItDoes() {
  const pillars = [
    {
      icon: <Phone size={28} />,
      title: 'Every inbound call becomes an opportunity',
      desc: 'A customer calls after hours or during a rush. Your agent answers, qualifies the buyer, handles objections naturally, and books the appointment — so your closer shows up to a warm conversation, not a cold call.',
      bullets: ['Answers after the 5th ring', 'Qualifies and books', 'Inbound + outbound', 'Handles objections naturally'],
      img: null,
    },
    {
      icon: <MessageSquare size={28} />,
      title: 'Revenue hiding in your CRM, found',
      desc: 'Dormant leads, cold contacts, customers who inquired months ago and never bought — SalibaLabs identifies them, reaches out with personalized messages, and brings them back into the pipeline.',
      bullets: ['Instant missed-call text-back', 'Multi-channel follow-up sequences', 'Reactivates cold CRM leads', 'Consistent cadence, zero burnout'],
      img: null,
    },
    {
      icon: <PlugZap size={28} />,
      title: 'Your team, amplified',
      desc: 'Every interaction — every call, text, and reactivation — surfaces automatically in DriveCentric and vAuto. Your team sees a full pipeline, not a mystery. They come in to close, not to chase.',
      bullets: ['DriveCentric CRM sync', 'vAuto / IMS integration', 'Call summaries + disposition', 'Full activity monitoring'],
      img: null,
    },
  ];

  return (
    <section id="product" data-testid="what-it-does-section" className="section-anchor py-24 lg:py-32 bg-[#0e0e0f]">
      <div className="gold-divider mb-0" />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] pt-16 lg:pt-24">
        <Reveal>
          <div className="text-center mb-16">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">What We Do</div>
            <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
              More opportunities. Same team.
            </h2>
            <p className="text-white/60 mt-4 max-w-xl mx-auto text-base leading-relaxed">
              SalibaLabs is an AI agent that works alongside your team — uncovering opportunities, reactivating dormant leads, and keeping every customer warm so your people can focus on closing.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          {pillars.map((pillar, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="card-hover rounded-2xl border border-white/8 bg-white/[0.025] p-6 h-full flex flex-col">
                {pillar.img && (
                  <div className="aspect-video rounded-xl overflow-hidden mb-5">
                    <img src={pillar.img} alt={pillar.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C' }}>
                  {pillar.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{pillar.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4 flex-1">{pillar.desc}</p>
                <ul className="space-y-2">
                  {pillar.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#C9A84C' }} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


// ─── TECH EDGE / LATENCY ─────────────────────────────────────────────────────
function TechEdge() {
  return (
    <section className="section-anchor py-20 lg:py-24 relative" style={{ background: '#080808' }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.35), transparent)' }} />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-4">Why Response Speed Wins Deals</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
                Latency is the conversation<br />
                <span className="gold-text">nobody talks about.</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-5">
                Latency is the time it takes for your agent to respond to a customer.
                Keep response time and delay both under 3 seconds and conversations feel natural.
                Go above that and you lose people — they hang up, move on, go to your competitor.
              </p>
              <p className="text-white/60 text-base leading-relaxed mb-6">
                There&rsquo;s another thing nobody talks about: <span className="text-white font-medium">memory.</span>{' '}
                When a system runs low on capacity, the agent starts losing context — forgetting what was said,
                slowing down, giving wrong answers. We only deploy on premium infrastructure
                so that never happens to your customer.
              </p>
              <div className="p-4 rounded-xl border border-[#C9A84C]/20 bg-[#C9A84C]/5">
                <p className="text-sm text-white/70 italic leading-relaxed">
                  &ldquo;We don&rsquo;t get cheap on the processing power. We deploy on the best servers
                  to reduce latency — so your customers always get the best experience.&rdquo;
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: '<500ms', l: 'Agent Response Time', sub: 'Human-feeling conversation' },
                { v: '<3s', l: 'Full Round Trip', sub: 'Response + delay covered' },
                { v: '100%', l: 'Context Retained', sub: 'Full conversation in memory' },
                { v: 'Tier 1', l: 'Server Infrastructure', sub: 'No shortcuts on processing' },
              ].map((m, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="rounded-2xl border border-white/8 bg-white/[0.025] p-5 card-hover">
                    <div className="text-3xl font-bold mb-1" style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}>{m.v}</div>
                    <div className="text-sm font-semibold text-white mb-1">{m.l}</div>
                    <div className="text-xs text-white/45">{m.sub}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── FEATURES GRID ──────────────────────────────────────────────────────────
function FeaturesGrid() {
  const features = [
    { icon: <Clock size={22} />, title: 'Inbound calls, handled 24/7', desc: 'Your agent answers after the 5th ring — qualifies the buyer, books the appointment, and keeps the opportunity warm.' },
    { icon: <RefreshCw size={22} />, title: 'Dormant leads, reactivated', desc: 'Contacts sitting in your CRM get a personalized outreach automatically. One message can unlock a deal that’s been sitting there for months.' },
    { icon: <MessageSquare size={22} />, title: 'Instant follow-up, every time', desc: 'The moment a call or inquiry comes in, your agent responds. Speed-to-lead is one of the biggest factors in conversion — this eliminates the delay.' },
    { icon: <Zap size={22} />, title: 'Qualified appointments, delivered', desc: 'Your agent qualifies, schedules, and delivers warm appointments to your team. They close deals — not chase leads.' },
    { icon: <BarChart2 size={22} />, title: 'Full pipeline visibility', desc: 'Every interaction logged in DriveCentric with call summaries and next-step recommendations. Your managers see the full picture in real time.' },
    { icon: <Mic size={22} />, title: 'Voicemail detected — action taken', desc: 'When a voicemail is left, it\'s identified instantly. The customer gets a personal follow-up text before they even hang up — keeping the conversation alive.' },
    { icon: <PhoneOff size={22} />, title: 'Hang-up? Follow-up sent.', desc: 'When someone calls and hangs up without connecting, they receive an immediate SMS. That window of interest doesn\'t close — it opens a conversation.' },
    { icon: <Shield size={22} />, title: 'Enhancement, not replacement', desc: "Your team is the closer. SalibaLabs is the finder. Together you convert more of what was already there." },
  ];

  return (
    <section data-testid="features-grid-section" className="section-anchor py-24 lg:py-32">
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="text-center mb-14">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">Capabilities</div>
            <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>What your agent does while your team focuses on closing.</h2>
            <p className="text-white/55 mt-3 max-w-xl mx-auto text-sm leading-relaxed">SalibaLabs runs 24/7 — finding opportunities, warming leads, and keeping your pipeline full so your team walks in ready to close.</p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="card-hover rounded-2xl border border-white/8 bg-white/[0.025] p-6 group">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:rotate-6"
                  style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C' }}
                >
                  {f.icon}
                </div>
                <h3 className="text-base font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{f.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── STATS ──────────────────────────────────────────────────────────────────
function Stats() {
  const stats = [
    { value: '24/7', label: 'Always Finding Opportunities', note: 'Nights, weekends, holidays included' },
    { value: '100%', label: 'Leads Touched', note: 'No more untouched CRM contacts' },
    { value: '<60s', label: 'Speed-to-Lead', note: 'First response in under a minute' },
    { value: '2 wks', label: 'To Go-Live', note: 'Fast setup, zero disruption to your team' },
  ];
  return (
    <section id="results" data-testid="stats-section" className="section-anchor py-24 lg:py-28 bg-[#0e0e0f]">
      <div className="gold-divider mb-0" />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] pt-16 lg:pt-24">
        <Reveal>
          <div className="text-center mb-14">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">By the Numbers</div>
            <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>More of what matters. Less left on the table.</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="rounded-2xl border border-white/8 bg-black/40 p-6 text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}>{s.value}</div>
                <div className="text-sm font-medium text-white mb-1">{s.label}</div>
                <div className="text-xs text-white/50">{s.note}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Car image accent */}
        <Reveal delay={0.2}>
          <div className="mt-12 rounded-2xl overflow-hidden" style={{ maxHeight: 320 }}>
            <img
              src="https://images.unsplash.com/photo-1757083434559-48b6ba82a69d?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Luxury car at night"
              className="w-full object-cover object-center"
              style={{ maxHeight: 320 }}
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.4), transparent)' }} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────
function FAQ() {
  const items = [
    {
      q: 'Does this replace my BDC or sales team?',
      a: 'No — and that\'s the whole point. SalibaLabs is an enhancement layer. Your team closes deals. Your agent finds them, warms them up, and delivers them. Think of it as giving every salesperson an always-on assistant who never runs out of energy.',
    },
    {
      q: 'What CRMs and IMS platforms are supported?',
      a: 'Built around DriveCentric CRM and vAuto (IMS) with full bi-directional sync. Every call, text, and interaction is logged automatically. If you use a different platform, let us know when you join the waitlist.',
    },
    {
      q: 'What exactly happens when a call is missed?',
      a: 'Within seconds, the customer receives a personalized text message. If they don\'t respond, a follow-up sequence kicks in. If they\'re a returning CRM contact, their history surfaces automatically so the next human conversation starts warm.',
    },
    {
      q: 'How fast can I get set up?',
      a: 'Typically 2 weeks from contract to Go-Live. That includes CRM/IMS integration, dedicated phone number setup, and a full onboarding walkthrough with your team. No disruption to your current workflow.',
    },
    {
      q: 'Is it TCPA compliant and opt-out safe?',
      a: 'Yes. All outbound communications respect opt-outs, include required disclosures, and are built to TCPA standards. Compliance is part of the architecture, not an add-on.',
    },
  ];

  const [open, setOpen] = useState(null);

  return (
    <section id="faq" data-testid="faq-section" className="section-anchor py-24 lg:py-32">
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="text-center mb-14">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">FAQ</div>
            <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Common questions. Straight answers.</h2>
          </div>
        </Reveal>
        <div className="max-w-2xl mx-auto space-y-3">
          {items.map((item, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div
                data-testid={`faq-item-${i}`}
                className="rounded-2xl border bg-white/[0.025] overflow-hidden transition-all duration-200"
                style={{ borderColor: open === i ? 'rgba(201,168,76,0.35)' : 'rgba(255,255,255,0.08)' }}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span className="font-medium text-white text-sm sm:text-base">{item.q}</span>
                  <span style={{ color: '#C9A84C' }}>
                    {open === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </span>
                </button>
                {open === i && (
                  <div className="px-5 pb-5 text-sm text-white/65 leading-relaxed border-t border-white/8 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section data-testid="final-cta-section" className="section-anchor py-24 lg:py-32 bg-[#0e0e0f]">
      <div className="gold-divider mb-0" />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] pt-16 lg:pt-24">
        <Reveal>
          <div className="text-center mb-12">
          {/* Updated final CTA copy — problem-focused */}
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">Limited Early Access</div>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              The opportunities are already there.<br /><span className="gold-text">Start finding them.</span>
            </h2>
            <p className="text-white/60 max-w-lg mx-auto text-base leading-relaxed">
              Join the waitlist today. We’re onboarding a select group of dealerships for the initial rollout.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="max-w-lg mx-auto">
            <WaitlistForm variant="final" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── TIKTOK FEATURE ──────────────────────────────────────────────────────────
function TikTokFeature() {
  return (
    <section className="py-20 lg:py-28 relative overflow-hidden" style={{ background: '#080808' }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)' }} />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left copy */}
          <Reveal>
            <div>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full border border-[#C9A84C]/25 bg-[#C9A84C]/8">
                <span className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase">As Seen on TikTok</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold mb-5 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
                George showed his audience first.
                <br />
                <span className="gold-text">Now it&rsquo;s your turn.</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-6">
                When George Saliba introduced SalibaLabs to his 1M+ followers,
                the response was immediate. Dealers from across the country reached out.
                This is the technology he&rsquo;s staking his reputation on — and he wanted
                his community to be the first to know.
              </p>
              <div className="space-y-3">
                {[
                  'Backed publicly by @georgejsaliba',
                  'Built for dealerships, not generic businesses',
                  'Dealer-first — George uses it himself',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-white/70">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C' }}>
                      <Star size={10} />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-8">
                <a
                  href="https://www.tiktok.com/@georgejsaliba"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 text-sm text-white/70 hover:text-white hover:border-[#C9A84C]/30 transition-all duration-200"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.52V6.76a4.85 4.85 0 01-1.02-.07z"/></svg>
                  Follow @georgejsaliba
                </a>
              </div>
            </div>
          </Reveal>

          {/* Right TikTok embed */}
          <Reveal delay={0.15}>
            <div className="flex justify-center lg:justify-end">
              <div
                className="relative rounded-2xl overflow-hidden w-full"
                style={{
                  maxWidth: 340,
                  boxShadow: '0 0 0 1px rgba(201,168,76,0.15), 0 32px 80px rgba(0,0,0,0.7)',
                }}
              >
                {/* Official TikTok embed */}
                <blockquote
                  className="tiktok-embed"
                  cite="https://www.tiktok.com/@georgejsaliba/video/7613872799844764941"
                  data-video-id="7613872799844764941"
                  style={{ maxWidth: '340px', minWidth: '280px', margin: 0 }}
                >
                  <section>
                    <a target="_blank" rel="noopener noreferrer" title="@georgejsaliba" href="https://www.tiktok.com/@georgejsaliba?refer=embed">@georgejsaliba</a>
                    <p>Talking to AI. What do you guys think? Drop your comments below.</p>
                  </section>
                </blockquote>
                <script async src="https://www.tiktok.com/embed.js" />
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </section>
  );
}


function TheReality() {
  const stats = [
    {
      stat: '78%',
      label: 'of sales go to the first responder',
      source: 'MIT Lead Response Study',
      color: '#C9A84C',
    },
    {
      stat: '>3 hrs',
      label: 'average dealership response time',
      source: 'Automotive industry average',
      color: '#C9A84C',
    },
    {
      stat: '50%',
      label: 'of leads never get a second follow-up',
      source: 'Dartnell Institute',
      color: '#C9A84C',
    },
    {
      stat: '<5%',
      label: 'of voicemails ever get returned',
      source: 'RingCentral Research',
      color: '#C9A84C',
    },
  ];

  return (
    <section className="section-anchor py-24 lg:py-32 relative overflow-hidden" style={{ background: '#080808' }}>
      {/* Accent glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)' }} />

      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full border border-red-500/20 bg-red-500/8">
              <AlertTriangle size={13} className="text-red-400" />
              <span className="font-mono-accent text-xs text-red-400 tracking-widest uppercase">The Honest Truth</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              The opportunity is already in your dealership.<br />
              <span className="gold-text">Most of it goes untouched.</span>
            </h2>
            <p className="text-white/55 max-w-xl mx-auto text-base leading-relaxed">
              These aren't George's numbers. These are industry-wide. Every dealership faces them.
              Most accept them. SalibaLabs changes them.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="rounded-2xl border border-white/8 bg-white/[0.025] p-6 text-center group card-hover">
                <div
                  className="text-5xl font-bold mb-3 tracking-tight"
                  style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}
                >
                  {s.stat}
                </div>
                <div className="text-sm font-medium text-white mb-2 leading-snug">{s.label}</div>
                <div className="font-mono-accent text-xs text-white/35">{s.source}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Bold statement */}
        <Reveal delay={0.2}>
          <div
            className="rounded-2xl p-8 lg:p-12 text-center relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(201,168,76,0.03) 100%)', border: '1px solid rgba(201,168,76,0.2)' }}
          >
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #C9A84C 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
            <div className="relative z-10">
              <p className="text-2xl sm:text-3xl font-semibold text-white leading-tight max-w-3xl mx-auto" style={{ fontFamily: 'Space Grotesk' }}>
                &ldquo;Your team isn&rsquo;t the problem.<br />
                <span style={{ color: '#C9A84C' }}>The system was never built to capture every opportunity.</span><br />
                Now it is.&rdquo;
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── HOW IT WORKS ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    {
      number: '01',
      icon: <Phone size={24} />,
      title: 'Your agent is always on',
      desc: 'A dedicated SalibaLabs agent is connected to your phone line, CRM, and IMS from day one. It works nights, weekends, and high-volume periods — the moments your team physically can\'t cover.',
      badge: '24/7 Coverage',
    },
    {
      number: '02',
      icon: <Target size={24} />,
      title: 'It finds and warms the opportunity',
      desc: 'Inbound calls, cold CRM contacts, unresponsive leads — your agent identifies every opportunity and makes the first move. It qualifies, answers questions, handles objections, and schedules.',
      badge: 'Opportunity Engine',
    },
    {
      number: '03',
      icon: <Users size={24} />,
      title: 'Your team closes',
      desc: 'Your salesperson receives a warm lead, a full call summary, and a booked appointment. They walk in with context, not cold air. No chasing. Just closing.',
      badge: 'Human Closes the Deal',
    },
  ];

  return (
    <section id="how" className="section-anchor py-24 lg:py-32" style={{ background: '#0e0e0f' }}>
      <div className="gold-divider mb-0" />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] pt-16 lg:pt-24">
        <Reveal>
          <div className="text-center mb-16">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">How It Works</div>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              Agent finds it. Human closes it.
            </h2>
            <p className="text-white/55 max-w-lg mx-auto text-base leading-relaxed">
              Three steps. Simple as that.
            </p>
          </div>
        </Reveal>

        <div className="relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-16 left-[16.66%] right-[16.66%] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.3) 20%, rgba(201,168,76,0.3) 80%, transparent)' }} />

          <div className="grid lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div className="relative flex flex-col items-center text-center lg:items-start lg:text-left">
                  {/* Step number + icon */}
                  <div className="relative mb-6">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-0 relative z-10"
                      style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)', color: '#C9A84C' }}
                    >
                      {step.icon}
                    </div>
                    <div
                      className="font-mono-accent text-xs font-bold absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px]"
                      style={{ background: '#C9A84C', color: '#0a0a0a' }}
                    >
                      {step.number}
                    </div>
                  </div>

                  <div
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold font-mono-accent tracking-widest uppercase mb-3"
                    style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.2)' }}
                  >
                    {step.badge}
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>{step.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Enhancement statement */}
        <Reveal delay={0.3}>
          <div className="mt-14 rounded-2xl border border-white/8 bg-white/[0.02] p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div
              className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center"
              style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C' }}
            >
              <Lightbulb size={20} />
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-1">This is enhancement, not replacement.</div>
              <div className="text-sm text-white/55 leading-relaxed">
                SalibaLabs doesn&rsquo;t sit in for your team. It sits beside them — handling the volume they can&rsquo;t, finding the leads they don&rsquo;t have time to chase, so when they show up, they&rsquo;re showing up to close.
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── NOT REPLACING — AMPLIFYING ──────────────────────────────────────────────
function NotReplacing() {
  const points = [
    { icon: <CheckCircle2 size={18} />, text: 'Your best closers stay your best closers — they just have more warm deals to close.' },
    { icon: <CheckCircle2 size={18} />, text: 'No more "I didn\'t have time to follow up" — that follow-up already happened.' },
    { icon: <CheckCircle2 size={18} />, text: 'Your BDC focuses on high-value conversations, not chasing cold contacts.' },
    { icon: <CheckCircle2 size={18} />, text: 'Every lead gets touched. Every opportunity gets a shot.' },
    { icon: <CheckCircle2 size={18} />, text: 'Managers see a full, real-time pipeline — not a mystery.' },
  ];

  return (
    <section className="section-anchor py-24 lg:py-28 relative overflow-hidden">
      {/* bg image accent */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url(https://images.pexels.com/photos/6584051/pexels-photo-6584051.jpeg?auto=compress&cs=tinysrgb&w=940)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(60%)',
        }}
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.92) 60%, rgba(10,10,10,0.85) 100%)' }} />

      <div className="relative z-10 mx-auto w-[min(1120px,calc(100%-32px))]">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div>
              <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-4">The Difference</div>
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>
                Not a robot.<br />
                Not a replacement.<br />
                <span className="gold-text">An upgrade to your team.</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-8">
                The best dealerships in the country aren&rsquo;t replacing their people with technology.
                They&rsquo;re using it to make their people better — so every salesperson has more
                opportunities, more context, and more time to do what they actually do best.
              </p>
              <p className="text-white/60 text-base leading-relaxed">
                That&rsquo;s exactly what George Saliba invested in. Not a gimmick.
                Not another SaaS dashboard nobody uses. A genuine operating upgrade
                for how modern dealerships capture and convert opportunities.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="space-y-3">
              {points.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/8 bg-white/[0.025]"
                >
                  <span className="mt-0.5 flex-shrink-0" style={{ color: '#C9A84C' }}>{p.icon}</span>
                  <span className="text-sm text-white/75 leading-relaxed">{p.text}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ──────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-white/8 py-10 bg-[#080808]">
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <div className="text-base font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            <span className="text-white">Saliba</span><span style={{ color: '#C9A84C' }}>Labs</span><span className="text-white/40">.ai</span>
          </div>
          <div className="text-xs text-white/40">AI built for the automotive industry.</div>
        </div>
        <div className="flex items-center gap-6">
          <a href="https://www.youtube.com/@georgejsaliba" target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 hover:text-white transition-colors">YouTube</a>
          <a href="https://www.instagram.com/georgejsaliba" target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 hover:text-white transition-colors">Instagram</a>
          <a href="/voice" className="text-xs text-white/50 hover:text-white transition-colors">Voice Platform →</a>
          <a href="/" className="text-xs text-white/50 hover:text-white transition-colors">← Home</a>
        </div>
        <div className="text-xs text-white/30">© {new Date().getFullYear()} SalibaLabs.ai. All rights reserved.</div>
      </div>
    </footer>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────
export default function LandingPage() {
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a', color: '#fafafa' }}>
      <Navbar onJoinClick={scrollToForm} />
      <Hero formRef={formRef} />
      <SocialProof />
      <TikTokFeature />
      <TheReality />
      <GeorgeStory />
      <WhatItDoes />
      <HowItWorks />
      <TechEdge />
      <FeaturesGrid />
      <NotReplacing />
      <Stats />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
